import React from 'react';
import { useSelector } from 'react-redux';
import { BarChart3, LineChart, Shuffle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import {
  MEAN_MAX_PENDING_REPORT_MESSAGE,
  meanMaxBlocksReportGeneration,
} from '../api/projectInformation';

interface ReportTypeOption {
  id: 'pipeData' | 'pipeCompatibility' | 'randomPipeCompatibility';
  title: string;
  description: string;
  icon: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
}

interface GenerateReportTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipeLabel: string;
  projectNumber?: string;
  onSelectPipeData: () => void;
  onSelectPipeCompatibility: () => void;
  onSelectRandomPipeCompatibility: () => void;
  compatibilityDisabled?: boolean;
  isBusy?: boolean;
}

export const GenerateReportTypeModal: React.FC<GenerateReportTypeModalProps> = ({
  open,
  onOpenChange,
  pipeLabel,
  projectNumber = '',
  onSelectPipeData,
  onSelectPipeCompatibility,
  onSelectRandomPipeCompatibility,
  compatibilityDisabled = false,
  isBusy = false,
}) => {
  const {
    meanMaxCalculationStatus,
    meanMaxStatusProjectNumber,
    meanMaxStatusError,
  } = useSelector((state: any) => state.pipefitterProject || {});

  const statusForProject =
    projectNumber && meanMaxStatusProjectNumber === projectNumber
      ? meanMaxCalculationStatus
      : null;
  const errorForProject =
    projectNumber && meanMaxStatusProjectNumber === projectNumber
      ? meanMaxStatusError
      : null;

  // Min/max gate applies to Pipe Compatibility Report only.
  const meanMaxBlocking =
    meanMaxStatusProjectNumber === projectNumber &&
    (Boolean(errorForProject) || meanMaxBlocksReportGeneration(statusForProject));

  const pipeCompatibilityDisabled = meanMaxBlocking || compatibilityDisabled;
  const pipeCompatibilityDisabledReason = meanMaxBlocking
    ? errorForProject ||
      `${MEAN_MAX_PENDING_REPORT_MESSAGE} Report generation is disabled until status is Completed${
        statusForProject ? ` (current: ${statusForProject})` : ''
      }.`
    : compatibilityDisabled
      ? 'Select pipes in the Pipe Details table first.'
      : undefined;

  const options: ReportTypeOption[] = [
    {
      id: 'pipeData',
      title: 'Pipe Data Report',
      description:
        'Min, max & average inner diameter and out-of-roundness (OOR) for each pipe.',
      icon: <BarChart3 className="h-5 w-5 text-hero-red" aria-hidden />,
    },
    {
      id: 'pipeCompatibility',
      title: 'Pipe Compatibility Report',
      description:
        "HiLO distribution by fitting method — where each method's joints land within spec.",
      icon: <LineChart className="h-5 w-5 text-blueprint-blue" aria-hidden />,
      disabled: pipeCompatibilityDisabled,
      disabledReason: pipeCompatibilityDisabledReason,
    },
    {
      id: 'randomPipeCompatibility',
      title: 'Random Pipe Compatibility Report',
      description:
        'How often random arrival orders still weld the line to completion under project fit rules.',
      icon: <Shuffle className="h-5 w-5 text-hero-red" aria-hidden />,
    },
  ];

  const handleSelect = (option: ReportTypeOption) => {
    if (isBusy || option.disabled) return;
    if (option.id === 'pipeData') {
      onSelectPipeData();
      return;
    }
    if (option.id === 'pipeCompatibility') {
      onSelectPipeCompatibility();
      return;
    }
    onSelectRandomPipeCompatibility();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isBusy && onOpenChange(next)}>
      <DialogContent className="max-w-4xl w-[95vw] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E2E5EA] text-left">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue">
            Generate Report
          </DialogTitle>
          <p className="text-[13px] text-heritage-grey mt-1">
            Which report do you want to generate for{' '}
            <span className="font-medium text-text-grey">{pipeLabel}</span>?
          </p>
        </DialogHeader>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {options.map((option) => {
            const disabled = isBusy || option.disabled;
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => handleSelect(option)}
                className={cn(
                  'flex flex-col items-start gap-3 rounded-xl border p-5 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hero-red focus-visible:ring-offset-2',
                  disabled
                    ? 'border-[#E2E5EA] bg-page-bg opacity-60 cursor-not-allowed'
                    : 'border-[#E2E5EA] bg-white hover:border-heritage-grey hover:bg-page-bg cursor-pointer'
                )}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[rgba(235,28,55,0.08)]">
                  {option.icon}
                </span>
                <span className="text-[15px] font-semibold text-blueprint-blue">{option.title}</span>
                <span className="text-[13px] text-heritage-grey leading-relaxed">
                  {option.description}
                </span>
                {option.disabled && option.disabledReason ? (
                  <span className="text-[12px] text-heritage-grey italic">{option.disabledReason}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
